import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../navbar";
import { useAuth } from "../../auth/AuthContext";
import GenericDataTable from "../DataCenter/GenericDataTable";
import "../Details.css";
import "./Settings.css";
import "./PaymentGatewayLog.css";

const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";
const ACTIVITY_API = "tablemate/endpoints/bill/paymentGatewayActivity.php";

function isoDateDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function escapeCsv(value) {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowsToCsv(rows) {
  const headers = [
    "id",
    "createdAt",
    "gatewayKey",
    "gatewayName",
    "eventType",
    "status",
    "httpStatus",
    "amount",
    "currency",
    "tabId",
    "tableNumber",
    "customer",
    "externalRef",
    "bywho",
    "source",
    "errorMessage",
    "requestSummary",
    "responseSummary",
  ];
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    lines.push(headers.map((h) => escapeCsv(row[h])).join(","));
  });
  return lines.join("\n");
}

function downloadBlob(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PaymentGatewayLog() {
  const pageTitle = "Payment Gateway Activity";
  const { tenantServerUrl, accessToken, selectedRestaurantId } = useAuth();

  const [fromDate, setFromDate] = useState(() => isoDateDaysAgo(30));
  const [toDate, setToDate] = useState(() => todayIso());
  const [status, setStatus] = useState("");
  const [eventType, setEventType] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tableRows = useMemo(
    () =>
      rows.map((r) => ({
        createdAt: r.createdAt,
        gateway: r.gatewayName || r.gatewayKey,
        gatewayKey: r.gatewayKey,
        eventType: r.eventType,
        status: r.status,
        amount: r.amount,
        table: r.tableNumber,
        customer: r.customer,
        bywho: r.bywho,
        source: r.source,
        externalRef: r.externalRef,
        error: r.errorMessage,
        httpStatus: r.httpStatus,
      })),
    [rows]
  );

  const fetchActivity = useCallback(async () => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set("id", selectedRestaurantId);
      params.set("limit", "1000");
      if (fromDate) params.set("from", fromDate);
      if (toDate) params.set("to", toDate);
      if (status) params.set("status", status);
      if (eventType) params.set("eventType", eventType);
      if (search.trim()) params.set("q", search.trim());

      const url = `${REDIR_BASE}${tenantServerUrl}/${ACTIVITY_API}?${params.toString()}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => "<no body>");
        throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
      }

      const data = await res.json();
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      setError(err.message || "Failed to load gateway activity");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    accessToken,
    selectedRestaurantId,
    tenantServerUrl,
    fromDate,
    toDate,
    status,
    eventType,
    search,
  ]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const handleExportCsv = () => {
    if (!rows.length) return;
    const csv = rowsToCsv(rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadBlob(`payment_gateway_activity_${stamp}.csv`, csv, "text/csv;charset=utf-8");
  };

  const handleExportJson = () => {
    if (!rows.length) return;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadBlob(
      `payment_gateway_activity_${stamp}.json`,
      JSON.stringify({ generatedAt: new Date().toISOString(), total, rows }, null, 2),
      "application/json"
    );
  };

  return (
    <>
      <Navbar pageTitle={pageTitle} />
      <div className="details-main">
        <section className="settings-card pga-log-card">
          <div className="settings-card-header pga-log-header">
            <div>
              <h2 className="settings-card-title">Payment gateway activity</h2>
              <p className="settings-main-label">
                Audit trail of online gateway selections and settlements. Export as CSV or JSON for
                reporting.
              </p>
            </div>
            <Link to="/settings" className="tm-btn tm-btn-light">
              Back to Settings
            </Link>
          </div>

          <div className="pga-filters">
            <label className="pga-filter">
              <span>From</span>
              <input
                className="settings-input"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>
            <label className="pga-filter">
              <span>To</span>
              <input
                className="settings-input"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>
            <label className="pga-filter">
              <span>Status</span>
              <select
                className="settings-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="ok">ok</option>
                <option value="error">error</option>
                <option value="pending">pending</option>
              </select>
            </label>
            <label className="pga-filter">
              <span>Event</span>
              <select
                className="settings-select"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
              >
                <option value="">All</option>
                <option value="settled">settled</option>
                <option value="staff_override">staff_override</option>
                <option value="selected">selected</option>
                <option value="initiate">initiate</option>
                <option value="callback">callback</option>
                <option value="fail">fail</option>
              </select>
            </label>
            <label className="pga-filter pga-filter-grow">
              <span>Search</span>
              <input
                className="settings-input"
                type="text"
                placeholder="Gateway, customer, table, ref…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>

          <div className="pga-actions">
            <button
              type="button"
              className="tm-btn tm-btn-primary"
              onClick={fetchActivity}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button
              type="button"
              className="tm-btn tm-btn-dark"
              onClick={handleExportCsv}
              disabled={!rows.length}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="tm-btn tm-btn-light"
              onClick={handleExportJson}
              disabled={!rows.length}
            >
              Export JSON
            </button>
            <span className="pga-total">{total} matching row(s)</span>
          </div>

          {error && (
            <div className="settings-field">
              <span className="settings-value settings-error">{error}</span>
            </div>
          )}

          {loading && !rows.length ? (
            <div className="settings-field">
              <span className="settings-value">Loading activity…</span>
            </div>
          ) : (
            <GenericDataTable rows={tableRows} defaultSortKey="createdAt" defaultSortDirection="desc" />
          )}
        </section>
      </div>
    </>
  );
}
